const SCORE_EXACT = 100;
const SCORE_STARTS_WITH = 80;
const SCORE_WORD_STARTS_WITH = 60;
const SCORE_CONTAINS = 40;
// Nota mais baixa que SCORE_CONTAINS de propósito - só entra quando não há
// NENHUM match por substring, então nunca ultrapassa um resultado "de
// verdade" nem se confirma sozinho no critério normal.
const SCORE_FUZZY = 20;

const DIACRITIC_RANGE_START = 0x0300;
const DIACRITIC_RANGE_END = 0x036f;

export const normalizeText = (text) => {
    if (!text) return '';

    const decomposed = text.toString().normalize('NFD');
    let withoutDiacritics = '';
    for (const char of decomposed) {
        const code = char.codePointAt(0);
        if (code >= DIACRITIC_RANGE_START && code <= DIACRITIC_RANGE_END) continue;
        withoutDiacritics += char;
    }

    return withoutDiacritics
        .replace(/-/g, ' ')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();
};

// Distância de edição (Levenshtein) entre duas strings - usada só como
// fallback de "escrita errada"/typo, quando não há nenhum match por
// substring de verdade.
const levenshteinDistance = (a, b) => {
    const al = a.length;
    const bl = b.length;
    if (al === 0) return bl;
    if (bl === 0) return al;

    let prevRow = Array.from({ length: bl + 1 }, (_, j) => j);

    for (let i = 1; i <= al; i++) {
        const currentRow = [i];
        for (let j = 1; j <= bl; j++) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1;
            currentRow[j] = Math.min(
                prevRow[j] + 1,        // remoção
                currentRow[j - 1] + 1, // inserção
                prevRow[j - 1] + cost  // substituição
            );
        }
        prevRow = currentRow;
    }

    return prevRow[bl];
};

// Quantos erros de digitação tolerar, proporcional ao tamanho do termo -
// termos curtos (ex.: siglas) toleram menos, senão qualquer coisa "parece"
// com eles.
const fuzzyThreshold = (length) => {
    if (length <= 4) return 1;
    if (length <= 8) return 2;
    return 3;
};

const isFuzzyMatch = (term, word) => {
    if (!term || !word) return false;
    const distance = levenshteinDistance(term, word);
    return distance <= fuzzyThreshold(Math.max(term.length, word.length));
};

export const matchScore = (query, fields = []) => {
    const normalizedQuery = normalizeText(query);
    if (!normalizedQuery) return 0;

    const terms = normalizedQuery.split(' ').filter(Boolean);
    if (terms.length === 0) return 0;

    // Versão "colada" (sem espaço nenhum) da query, pra cobrir o caso do usuário
    // digitar um nome composto sem hífen E sem espaço (ex: "kothrine" em vez de
    // "k-othrine"/"k othrine") — o replace de hífen por espaço sozinho não cobre isso.
    const joinedQuery = normalizedQuery.replace(/\s+/g, '');

    let bestScore = null;

    fields.forEach((field, fieldIndex) => {
        if (!field) return;
        const normalizedField = normalizeText(field);
        if (!normalizedField) return;

        const joinedField = normalizedField.replace(/\s+/g, '');
        const fieldWords = normalizedField.split(' ');

        const allTermsMatch = terms.every(term => normalizedField.includes(term));
        const joinedMatch = joinedQuery.length > 0 && joinedField.includes(joinedQuery);

        let score;
        if (allTermsMatch || joinedMatch) {
            if (normalizedField === normalizedQuery || joinedField === joinedQuery) {
                score = SCORE_EXACT;
            } else if (normalizedField.startsWith(normalizedQuery) || joinedField.startsWith(joinedQuery)) {
                score = SCORE_STARTS_WITH;
            } else if (fieldWords.some(word => word.startsWith(normalizedQuery))) {
                score = SCORE_WORD_STARTS_WITH;
            } else {
                score = SCORE_CONTAINS;
            }
        } else {
            // Nenhum match por substring - último recurso, tolerante a erro de
            // digitação: só entra (com nota mais baixa) se TODO termo digitado
            // tiver alguma palavra "parecida" (por distância de edição) no
            // campo - ex.: "santa catrina" ainda acha "Santa Catarina".
            const allTermsFuzzyMatch = terms.every(term =>
                fieldWords.some(word => isFuzzyMatch(term, word))
            );
            if (!allTermsFuzzyMatch) return;
            score = SCORE_FUZZY;
        }

        // campos mais tardios (sinônimos/princípio ativo) desempatam com peso um pouco menor
        // que o nome principal, sem impedir que apareçam nos resultados.
        score -= fieldIndex;

        if (bestScore === null || score > bestScore) {
            bestScore = score;
        }
    });

    return bestScore;
};

export const filterAndRankOptions = (options, query, getSearchFields) => {
    if (!Array.isArray(options)) return [];
    if (!normalizeText(query)) return options;

    return options
        .map(option => ({ option, score: matchScore(query, getSearchFields(option)) }))
        .filter(({ score }) => score !== null)
        .sort((a, b) => b.score - a.score)
        .map(({ option }) => option);
};
